import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { AstroUser, LoginSignUpResponse } from 'common/services/auth-service';
import { BasicUser, SessionState } from './types';

import { translate } from 'common/config/i18n';
import { Option } from 'common/types/global';

import { RootState } from '../store';

const initialState: SessionState = {};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetStore: (state) => {
      state = initialState;
      return state;
    },
    setUser: (state, action: PayloadAction<AstroUser | undefined>) => {
      state.user = action.payload;
      state.basicUser = action.payload
        ? {
            userId: action.payload.idUser,
            firstName: action.payload.firstName,
            lastName: action.payload.lastName,
            mobileNumber: action.payload.phoneNumber,
            country: action.payload.country,
          }
        : undefined;
    },
    setBasicUser: (state, action: PayloadAction<BasicUser | undefined>) => {
      state.basicUser = action.payload;
    },
    setPhoneNumber: (state, action: PayloadAction<string | undefined>) => {
      state.phoneNumber = action.payload;
    },
    setDialCode: (state, action: PayloadAction<string | undefined>) => {
      state.dialCode = action.payload;
    },
    setValidationData: (state, action: PayloadAction<LoginSignUpResponse | undefined>) => {
      state.validationData = action.payload;
    },
  },
});

// Selectors
const getSession = (state: RootState): SessionState => state.session;
export const getUser = createSelector(getSession, (session) => session.user);
export const getBasicUser = createSelector(getSession, (session) => session.basicUser);
export const getDocumentTypes = createSelector(getUser, (user) =>
  user && user.userCountry
    ? user.userCountry.documentTypeOptions.map((dt) => ({
        text: dt.value === 'DEFAULT' ? translate('mobile_document_number') : dt.value,
        value: dt.documentType,
      }))
    : ([] as Option[]),
);
export const getStates = createSelector(getUser, (user) =>
  user && user.userCountry
    ? user.userCountry.states.map((dt) => ({
        text: dt.displayName,
        value: dt.code,
      }))
    : ([] as Option[]),
);
export const getPhoneNumber = createSelector(getSession, (session) => session.phoneNumber);
export const getDialCode = createSelector(getSession, (session) => session.dialCode);
export const getValidationData = createSelector(getSession, (session) => session.validationData);

const { actions, reducer } = sessionSlice;

export const {
  setUser,
  setBasicUser,
  resetStore: sessionReset,
  setPhoneNumber,
  setValidationData,
  setDialCode,
} = actions;

export default reducer;
